import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import { SurveyContext } from "../context/SurveyContext";
import api from "../services/api";
import { getProfilePicture } from "../utils/avatar";
import CommunityProfileCard from "./CommunityProfileCard";
import { FaSpinner } from "react-icons/fa";

const CommunityMap = ({
  onFollowChange,
  followedUsers = [],
  stanceLabels,
  proLabel = "Support",
  contraLabel = "Oppose",
  compact = false,
  context = "onboarding", // "onboarding" or "app"
}) => {
  const { instanceId } = useContext(SurveyContext);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [hoveredUser, setHoveredUser] = useState(null);
  const [, forceUpdate] = useState(0); // Forces re-read of DOM dimensions on hover
  const containerRef = useRef(null);
  const avatarRefs = useRef({});
  const [containerSize, setContainerSize] = useState({ w: 800, h: 500 });

  // Hover handler that forces dimension re-read for arrow alignment
  const handleHover = useCallback((agent) => {
    setHoveredUser(agent);
    forceUpdate(n => n + 1);
  }, []);

  const fetchAgents = useCallback(async () => {
    if (!instanceId) return;
    try {
      const token = sessionStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await api.get("/users/community-map", { params: { instanceId }, headers });
      setAgents(data || []);
    } catch (e) { console.warn("Failed to load community map:", e.message); }
    finally { setLoading(false); }
  }, [instanceId]);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  useEffect(() => {
    if (followedUsers.length > 0)
      setAgents((prev) => prev.map((a) => ({ ...a, isFollowedByMe: followedUsers.includes(a._id) })));
  }, [followedUsers]);

  useEffect(() => {
    if (!containerRef.current) return;
    const updateSize = () => {
      if (!containerRef.current) return;
      // Use clientWidth/clientHeight — matches CSS percentage positioning base
      // (excludes border, includes padding — same reference as left: X%)
      const cw = containerRef.current.clientWidth;
      const ch = containerRef.current.clientHeight;
      if (cw > 0) setContainerSize({ w: cw, h: ch });
    };
    const obs = new ResizeObserver(updateSize);
    obs.observe(containerRef.current);
    updateSize(); // Initial measurement
    return () => obs.disconnect();
  }, []);

  const handleFollow = async (userId) => {
    const agent = agents.find((a) => a._id === userId);
    if (!agent) return;
    try {
      const token = sessionStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      if (agent.isFollowedByMe) await api.post("/follow/unfollow", { followeeId: userId, instanceId }, { headers });
      else await api.post("/follow", { followeeId: userId, instanceId }, { headers });
      setAgents((prev) => prev.map((a) => a._id === userId ? { ...a, isFollowedByMe: !a.isFollowedByMe } : a));
      if (selectedUser?._id === userId) setSelectedUser((prev) => ({ ...prev, isFollowedByMe: !prev.isFollowedByMe }));
      if (onFollowChange) {
        const nf = agent.isFollowedByMe ? followedUsers.filter((id) => id !== userId) : [...followedUsers, userId];
        onFollowChange(nf);
      }
    } catch (e) { console.error("Follow/unfollow failed:", e.message); }
  };

  const positioned = useMemo(() => {
    if (agents.length === 0) return [];
    const maxFollowers = Math.max(1, ...agents.map((a) => a.followerCount || 1));
    const minSize = compact ? 28 : 32;
    const maxSize = compact ? 60 : 78;
    const getSize = (a) => Math.round(minSize + ((a.followerCount || 1) / maxFollowers) * (maxSize - minSize));

    const pro = agents.filter((a) => a.stance === "pro").sort((a, b) => (b.followerCount || 0) - (a.followerCount || 0));
    const contra = agents.filter((a) => a.stance === "contra").sort((a, b) => (b.followerCount || 0) - (a.followerCount || 0));

    // Layout: two camps with golden angle spiral per camp
    // Influencers at each camp's center, regular agents spiral outward
    const goldenAngle = Math.PI * (3 - Math.sqrt(5));

    const layoutCamp = (list, centerX, centerY) => {
      return list.map((agent, i) => {
        const size = getSize(agent);
        const rank = i / Math.max(list.length - 1, 1);
        const radius = 2 + rank * 22;
        const angle = i * goldenAngle;
        return {
          ...agent,
          x: centerX + radius * Math.cos(angle),
          y: centerY + radius * Math.sin(angle) * 0.9,
          size,
        };
      });
    };

    let all = [
      ...layoutCamp(contra, 30, 50),
      ...layoutCamp(pro, 70, 50),
    ];

    // Collision resolution: push overlapping agents apart
    // Iterate a few times to settle positions
    for (let iter = 0; iter < 30; iter++) {
      for (let i = 0; i < all.length; i++) {
        for (let j = i + 1; j < all.length; j++) {
          const a = all[i], b = all[j];
          const dx = b.x - a.x, dy = b.y - a.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          // Minimum distance: sum of radii (in %) + padding for label text
          const minDist = (a.size + b.size) / 2 * 0.22 + 4.5;
          if (dist < minDist && dist > 0.1) {
            const push = (minDist - dist) / 2;
            const nx = dx / dist, ny = dy / dist;
            a.x -= nx * push;
            a.y -= ny * push;
            b.x += nx * push;
            b.y += ny * push;
          }
        }
      }
    }

    // Clamp to bounds
    all = all.map((a) => ({
      ...a,
      x: Math.max(6, Math.min(94, a.x)),
      y: Math.max(8, Math.min(92, a.y)),
    }));

    return all;
  }, [agents, compact]);

  if (loading) {
    return <div className="flex items-center justify-center py-12"><FaSpinner className="animate-spin w-8 h-8 text-spiders-primary-400" /></div>;
  }

  const mapHeight = compact ? 450 : 580;

  // Which connections to show (only for hovered agent)
  const hoveredId = hoveredUser?._id;
  const hoveredConnections = hoveredId
    ? positioned.filter((a) => {
        const hAgent = positioned.find((p) => p._id === hoveredId);
        if (!hAgent) return false;
        return (hAgent.following || []).some((fid) => fid === a._id || fid === a._id?.toString())
          || (a.following || []).some((fid) => fid === hoveredId || fid === hoveredId?.toString());
      }).map((a) => a._id)
    : [];

  const stanceHex = {
    pro: "var(--spiders-accent-400, #f59e0b)",
    contra: "var(--spiders-primary-400, #2dd4bf)",
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2 px-4">
        <span className="text-xs font-bold text-spiders-primary-400 uppercase tracking-wider">{contraLabel}</span>
        <div className="flex-1 mx-6 h-px bg-spiders-border/30" />
        <span className="text-xs font-bold text-spiders-accent-400 uppercase tracking-wider">{proLabel}</span>
      </div>

      <div
        ref={containerRef}
        className="relative rounded-xl border border-spiders-border/30 overflow-hidden"
        style={{ height: mapHeight, background: "radial-gradient(ellipse at center, rgba(30,30,42,0.9) 0%, rgba(18,18,28,0.95) 100%)" }}
      >
        {/* Subtle stance gradient zones */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "linear-gradient(90deg, rgba(45,212,191,0.03) 0%, transparent 35%, transparent 65%, rgba(245,158,11,0.03) 100%)"
        }} />

        {/* Connection lines — only shown when hovering an agent */}
        {hoveredId && (() => {
          const hAgent = positioned.find((p) => p._id === hoveredId);
          if (!hAgent) return null;
          const cRect = containerRef.current?.getBoundingClientRect();
          if (!cRect || cRect.width <= 0) return null;

          // Read actual avatar center positions from the DOM — no coordinate math
          const center = (id) => {
            const el = avatarRefs.current[id];
            if (!el) return null;
            const r = el.getBoundingClientRect();
            return { cx: r.left + r.width / 2 - cRect.left, cy: r.top + r.height / 2 - cRect.top };
          };

          const makeLine = (fromId, toId, key, outgoing) => {
            const from = center(fromId), to = center(toId);
            if (!from || !to) return null;
            const fromAgent = positioned.find((a) => a._id === fromId);
            const toAgent = positioned.find((a) => a._id === toId);
            const dx = to.cx - from.cx, dy = to.cy - from.cy;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < 20) return null;
            const nx = dx / dist, ny = dy / dist;
            const r1 = (fromAgent?.size || 40) / 2 + 3;
            const r2 = (toAgent?.size || 40) / 2 + 3;
            return (
              <line key={key}
                x1={from.cx + nx * r1} y1={from.cy + ny * r1}
                x2={to.cx - nx * (r2 + 4)} y2={to.cy - ny * (r2 + 4)}
                stroke={stanceHex[fromAgent?.stance]}
                strokeWidth={outgoing ? 2 : 1.5}
                strokeOpacity={outgoing ? 0.5 : 0.35}
                strokeDasharray={outgoing ? undefined : "4 3"}
                markerEnd={outgoing ? "url(#arr-hover)" : "url(#arr-hover-in)"}
              />
            );
          };

          const lines = [];
          (hAgent.following || []).forEach((targetId) => {
            const target = positioned.find((a) => a._id === targetId || a._id?.toString() === targetId);
            if (target) lines.push(makeLine(hAgent._id, target._id, `out-${targetId}`, true));
          });
          positioned.forEach((other) => {
            if (other._id === hoveredId) return;
            const follows = (other.following || []).some((fid) => fid === hoveredId || fid === hoveredId?.toString());
            if (follows) lines.push(makeLine(other._id, hAgent._id, `in-${other._id}`, false));
          });

          return (
            <svg
              className="absolute pointer-events-none"
              style={{ zIndex: 1, left: 0, top: 0, width: cRect.width, height: cRect.height }}
            >
              <defs>
                <marker id="arr-hover" viewBox="0 0 10 7" refX="9" refY="3.5" markerWidth="8" markerHeight="6" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#d1d5db" fillOpacity="0.8" />
                </marker>
                <marker id="arr-hover-in" viewBox="0 0 10 7" refX="9" refY="3.5" markerWidth="7" markerHeight="5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#d1d5db" fillOpacity="0.6" />
                </marker>
              </defs>
              {lines}
            </svg>
          );
        })()}

        {/* Agent avatars */}
        {positioned.map((agent) => {
          const borderClass = agent.stance === "pro" ? "border-spiders-accent-400" : "border-spiders-primary-400";
          const ringClass = agent.stance === "pro" ? "ring-spiders-accent-400/30" : "ring-spiders-primary-400/30";
          const isConnected = hoveredId && (hoveredConnections.includes(agent._id) || agent._id === hoveredId);
          const isDimmed = hoveredId && !isConnected;

          return (
            <button
              key={agent._id}
              onClick={() => setSelectedUser(agent)}
              onMouseEnter={() => handleHover(agent)}
              onMouseLeave={() => handleHover(null)}
              className={`absolute transform -translate-x-1/2 -translate-y-1/2 group transition-all duration-200 hover:scale-110 ${
                isDimmed ? "opacity-30" : ""
              }`}
              style={{ left: `${agent.x}%`, top: `${agent.y}%`, zIndex: agent._id === hoveredId ? 50 : 10 + agent.size }}
            >
              <img
                ref={(el) => { avatarRefs.current[agent._id] = el; }}
                src={getProfilePicture(agent.username, agent.avatarSeed)}
                alt={agent.username}
                className={`rounded-full border-[3px] transition-all ${
                  agent.isFollowedByMe
                    ? `${borderClass} ring-2 ${ringClass} brightness-110 shadow-xl`
                    : `${borderClass} shadow-lg ${isDimmed ? "" : "group-hover:shadow-xl"}`
                }`}
                style={{ width: agent.size, height: agent.size }}
              />
              <span
                className={`block text-center leading-tight mt-1 transition-all whitespace-nowrap ${
                  agent.size > 58 ? "text-xs font-medium" : "text-[11px]"
                } ${
                  agent.isFollowedByMe
                    ? "text-spiders-text-primary font-semibold"
                    : isDimmed ? "opacity-0" : "text-spiders-text-secondary group-hover:text-spiders-text-primary"
                }`}
                style={{ textShadow: "0 1px 4px rgba(0,0,0,0.9), 0 0 10px rgba(0,0,0,0.8)" }}
              >
                @{agent.username.length > 13 ? agent.username.slice(0, 12) + "…" : agent.username}
              </span>
            </button>
          );
        })}
      </div>

      {selectedUser && (
        <CommunityProfileCard
          user={selectedUser}
          onFollow={handleFollow}
          onClose={() => setSelectedUser(null)}
          stanceLabels={stanceLabels}
          context={context}
        />
      )}
    </div>
  );
};

export default CommunityMap;
